import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Splash from '@/components/molecules/splash/splash';
import Link from '@/components/utilities/link/link';
import useIsHovered from '@/libraries/hooks/use-is-hovered';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './full-teaser-card.module.scss';

export interface FullTeaserCardProps {
  /** The class name to apply */
  className?: string;
  /** The primary header of the teaser */
  headline: Content.Components['FullTeaserCardDto']['headline'];
  /** Whether to hide the gradient */
  hideGradient?: Content.Components['FullTeaserCardDto']['hideGradient'];
  /** The background image of the teaser */
  image: Content.Components['FullTeaserCardDto']['image'];
  /** The link to show */
  link?: Content.Components['FullTeaserCardDto']['link'];
  /** The splash to show on the card */
  splash?: Content.Components['FullTeaserCardDto']['splash'];
  /** The secondary header of the teaser */
  subHeadline: Content.Components['FullTeaserCardDto']['subHeadline'];
}

const MAXIMUM_CONTENT_HEIGHT_MOBILE = 340;
const MAXIMUM_CONTENT_HEIGHT_TABLET = 250;
const MAXIMUM_CONTENT_HEIGHT_LAPTOP = 400;
const OFFSET_MOBILE = 118;
const OFFSET_TABLET = 126;
const OFFSET_LAPTOP = 176;

const FullTeaserCard = ({
  className,
  headline,
  hideGradient = false,
  image,
  link,
  splash,
  subHeadline,
}: FullTeaserCardProps) => {
  const isTablet = useWindowSize('tablet');
  const isLaptop = useWindowSize('laptop');
  const contentRef = useRef<HTMLDivElement>(null);
  const {
    bindRef,
    elementRef: componentRef,
    isHovered,
  } = useIsHovered<HTMLAnchorElement>();

  const setCardHeight = (maxHeight, offset) => {
    if (!componentRef?.current || !contentRef?.current) return;

    const contentHeight = contentRef.current?.offsetHeight;
    const component = componentRef.current;

    if (contentHeight > maxHeight) {
      component.style.height = `${contentHeight + offset}px`;
    }
  };

  useEffect(() => {
    if (!isTablet) {
      setCardHeight(MAXIMUM_CONTENT_HEIGHT_MOBILE, OFFSET_MOBILE);
    }

    if (isTablet && !isLaptop) {
      setCardHeight(MAXIMUM_CONTENT_HEIGHT_TABLET, OFFSET_TABLET);
    }

    if (isTablet && isLaptop) {
      setCardHeight(MAXIMUM_CONTENT_HEIGHT_LAPTOP, OFFSET_LAPTOP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTablet, isLaptop]);

  return (
    <Link
      ref={bindRef}
      className={classNames(styles.component, className, {
        [styles.hasLink]: !!link,
      })}
      url={link ? link.url : undefined}
    >
      <Image
        alt={image.altText}
        className={styles.image}
        focalPoint={image.focalPoint}
        height={458}
        src={image.url}
        srcSet={{
          desktop: {
            height: 576,
            width: 1440,
          },
          laptop: {
            height: 576,
            width: 1024,
          },
          tablet: {
            height: 376,
            width: 670,
          },
        }}
        unoptimized
        width={340}
      />

      {!hideGradient && <div className={styles.overlay} />}

      {splash && <Splash className={styles.splash} {...splash} />}

      <div ref={contentRef} className={styles.content}>
        <Typography
          className={styles.headline}
          tag="h2"
          tagStyle="displayLarge"
        >
          {headline}
        </Typography>

        <Typography
          className={styles.subHeadline}
          tag="p"
          tagStyle="headingSmall"
        >
          {subHeadline}
        </Typography>

        {link && (
          <div className={styles.buttonWrapper}>
            <Button
              hovered={isHovered}
              tabIndex={-1}
              text={link.text}
              variant="primary"
            />
          </div>
        )}
      </div>
    </Link>
  );
};

FullTeaserCard.displayName = 'FullTeaserCard';

export default FullTeaserCard;
